import React, { useEffect, useState } from "react";
import {
  Drawer,
  Descriptions,
  Button,
  Tag,
  Divider,
  Select,
  message,
  Modal,
  Tooltip,
  Typography,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  moveToNextRoundThunk,
  assignHRThunk,
  rejectCandidateThunk,
  updateRoundCommunicationThunk,
} from "../../../Store/Slices/Admin/jobApplication/jobApplicationThunks";

import { fetchAllStaff } from "../../../Store/Slices/Admin/Users/Staff/staff.action";
import ProtectedAction from "../../../Routes/ProtectedRoutes/ProtectedAction";
import { PERMISSIONS } from "../../../config/permission";
import { EyeOutlined, PlusOutlined } from "@ant-design/icons";

const { Text } = Typography;

const { Option } = Select;

const ApplicationDetailsDrawer = ({ application, onClose }) => {
  const dispatch = useDispatch();
  const { staff } = useSelector((store) => store.admin.all_staff);

  const [selectedHR, setSelectedHR] = useState(null);

  useEffect(() => {
    if (!staff?.length) {
      dispatch(fetchAllStaff());
    }
  }, [dispatch, staff]);

  const currentRound = application.roundHistory?.find(
    (r) => r.roundName === application.currentRound,
  );

  const handleMoveNext = async () => {
    try {
      await dispatch(moveToNextRoundThunk(application._id)).unwrap();
      message.success("Candidate moved to the next round successfully");
    } catch (error) {
      message.error(
        error?.message || "Failed to move candidate to the next round",
      );
    }
  };

  const handleAssignHR = async () => {
    if (!selectedHR) {
      message.warning("Please select an HR first");
      return;
    }

    try {
      await dispatch(
        assignHRThunk({
          id: application._id,
          staffId: selectedHR,
        }),
      ).unwrap();

      message.success("HR assigned successfully");
    } catch (error) {
      message.error(error?.message || "Failed to assign HR");
    }
  };

  const handleReject = async () => {
    try {
      await dispatch(
        rejectCandidateThunk({
          id: application._id,
          performanceSummary: "Rejected by admin",
          overallStatus: "rejected",
          isRejected: true,
        }),
      ).unwrap();

      message.success("Candidate rejected successfully");
      onClose();
    } catch (error) {
      message.error(error?.message || "Failed to reject candidate");
    }
  };

  const handleApprove = async () => {
    try {
      await dispatch(
        rejectCandidateThunk({
          id: application._id,
          performanceSummary: "Approved by admin",
          overallStatus: "approved",
          isRejected: false,
        }),
      ).unwrap();

      message.success("Candidate approved successfully");
      onClose();
    } catch (error) {
      message.error(error?.message || "Failed to approve candidate");
    }
  };

  const handleSendMail = () => {
    Modal.confirm({
      title: "Increase Email Count?",
      content:
        "Are you sure you want to increase the email count for this candidate?",
      okText: "Yes, Increase",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await dispatch(
            updateRoundCommunicationThunk({
              applicationId: application._id,
              roundId: currentRound._id,
              type: "mail",
            }),
          ).unwrap();

          message.success("Email count updated successfully");
        } catch (error) {
          message.error("Failed to update email count");
        }
      },
    });
  };

  const handleCallCandidate = () => {
    Modal.confirm({
      title: "Increase Call Count?",
      content:
        "Are you sure you want to increase the call count for this candidate?",
      okText: "Yes, Increase",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await dispatch(
            updateRoundCommunicationThunk({
              applicationId: application._id,
              roundId: currentRound._id,
              type: "call",
            }),
          ).unwrap();

          message.success("Call count updated successfully");
        } catch (error) {
          message.error("Failed to update call count");
        }
      },
    });
  };

  return (
    <Drawer title="Applicant" open={true} onClose={onClose} width={"60%"}>
      <Descriptions column={1} bordered size="small">
        {application.formData &&
          Object.entries(application.formData).map(([key, value]) => {
            const formattedLabel = key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase());

            const lowerKey = key.toLowerCase();

            const isCopyField =
              lowerKey.includes("name") ||
              lowerKey.includes("email") ||
              lowerKey.includes("contact") ||
              lowerKey.includes("phone");

            const isHttpsLink =
              typeof value === "string" && value.startsWith("https");

            return (
              <Descriptions.Item key={key} label={formattedLabel}>
                {isHttpsLink ? (
                  <Tooltip title="View Link">
                    <EyeOutlined
                      style={{
                        fontSize: 18,
                        cursor: "pointer",
                        color: "#1677ff",
                      }}
                      onClick={() => window.open(value, "_blank")}
                    />
                  </Tooltip>
                ) : isCopyField ? (
                  <Text copyable={{ text: value }}>{value || "-"}</Text>
                ) : (
                  value || "-"
                )}
              </Descriptions.Item>
            );
          })}
      </Descriptions>

      <Divider />

      <h3>Round History</h3>

      {application.roundHistory?.map((round, index) => (
        <Descriptions
          key={index}
          column={1}
          bordered
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Descriptions.Item label="Round">{round.roundName}</Descriptions.Item>

          <Descriptions.Item label="Status">{round.status}</Descriptions.Item>

          <Descriptions.Item label="Score">
            {round.roundScore} / {round.maxScore}
          </Descriptions.Item>

          <Descriptions.Item label="Rating">
            {round.rating || "-"}
          </Descriptions.Item>

          <Descriptions.Item label="Mail Count">
            <div className="flex justify-between">
              {round.mailCount}

              {currentRound?._id === round._id && (
                <Tooltip title="Add Mail Count">
                  <Button
                    size="small"
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={handleSendMail}
                  />
                </Tooltip>
              )}
            </div>
          </Descriptions.Item>

          <Descriptions.Item label="Call Count">
            <div className="flex justify-between">
              {round.callCount}

              {currentRound?._id === round._id && (
                <Tooltip title="Add Call Count">
                  <Button
                    size="small"
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={handleCallCandidate}
                  />
                </Tooltip>
              )}
            </div>
          </Descriptions.Item>
        </Descriptions>
      ))}

      {/* {currentRound && (
        <>
          <Divider />
          <h3>Communication</h3>

          <div style={{ display: "flex", gap: 10 }}>
            <Button type="default" block onClick={handleSendMail}>
              Email Sent Count
            </Button>

            <Button type="default" block onClick={handleCallCandidate}>
              Call Count
            </Button>
          </div>
        </>
      )} */}

      <Divider />

      <ProtectedAction requiredPermission={PERMISSIONS.ASSIGN_HR_JOB}>
        <Select
          placeholder="Select HR"
          style={{ width: "100%", marginBottom: 12 }}
          showSearch
          optionFilterProp="label"
          onChange={(value) => setSelectedHR(value)}
          options={staff?.map((s) => ({
            value: s._id,
            label: `${s.firstName} ${s.lastName}`,
          }))}
        />

        <Button
          type="default"
          block
          style={{ marginBottom: 10 }}
          onClick={handleAssignHR}
          disabled={!selectedHR || application.isRejected}
        >
          Assign HR
        </Button>
      </ProtectedAction>

      <ProtectedAction requiredPermission={PERMISSIONS.MOVE_TO_NEXT_ROUND}>
        <Button
          type="primary"
          block
          style={{ marginBottom: 10 }}
          onClick={handleMoveNext}
          disabled={application.isRejected}
        >
          Move To Next Round
        </Button>
      </ProtectedAction>

      <ProtectedAction
        requiredPermission={PERMISSIONS.APRROVE_REJECT_CANDIDATE}
      >
        <div className="flex gap-x-5">
          <Button
            type="primary"
            style={{
              background: "#16a34a",
              borderColor: "#16a34a",
              marginBottom: 10,
            }}
            block
            onClick={handleApprove}
          >
            Approve Candidate
          </Button>

          <Button
            danger
            block
            onClick={handleReject}
            disabled={application.isRejected}
          >
            Reject Candidate
          </Button>
        </div>
      </ProtectedAction>
    </Drawer>
  );
};

export default ApplicationDetailsDrawer;
